// Build-in hooks
import { useEffect, useState } from "react";

// Import necessary icons
import { IoMdClose } from "react-icons/io";

// Import custom components
import QueueTrackRow from "../Track/QueueTrackRow";

// Import context for player state management
import { useSupremeEditsPlayer } from "../../contexts/SupremeEditsContext";

const PlayerQueue = ({ isOpen, onClose, autoPlay, setAutoPlay }) => {
	const [shouldRender, setShouldRender] = useState(isOpen); // State to control rendering of the component
	const [fadeClass, setFadeClass] = useState("animate-top-to-bottom-opacity"); // State to manage animation classes
	const [leftPosition, setLeftPosition] = useState("2px");
	const [contentHeight, setContentHeight] = useState("253px");
	const [containerHeight, setContainerHeight] = useState("auto");

	useEffect(() => {
		const calculateDimensions = () => {
			const width = window.innerWidth;
			const height = window.innerHeight;

			// Calculate left position
			if (width < 640) {
				setLeftPosition("max(2px, calc(100vw - 483px))");
			} else if (width < 768) {
				setLeftPosition("max(14px, calc(100vw - 543px))");
			} else if (width < 1024) {
				setLeftPosition("max(16px, calc(100vw - 583px))");
			} else if (width < 1280) {
				setLeftPosition("max(16px, calc(50vw - 453px))");
			} else {
				setLeftPosition("max(16px, calc(50vw - 483px))");
			}

			// Calculate content height based on track row heights and screen size
			// Track row heights: 45px (sm), 50px (md), 60px (lg)
			// Header heights: ~40px (sm), ~45px (md), ~55px (lg)
			// Bottom player heights: 205px (sm), 215px (md), 106px (lg)
			
			let trackRowHeight, headerHeight, bottomPlayerHeight, maxTracks;
			
			if (width >= 1024) {
				// Large screens - keep current behavior (432px content height)
				trackRowHeight = 78;
				headerHeight = 64;
				bottomPlayerHeight = 113;
				maxTracks = 5;
			}else if (width >= 768){
				trackRowHeight	= 60;
				headerHeight=48;
				bottomPlayerHeight=206;
				maxTracks = Math.min(7, Math.floor((height - bottomPlayerHeight - headerHeight) / trackRowHeight));
			} else if (width >= 640) {
				// Medium screens - calculate based on available space
				trackRowHeight = 50;
				headerHeight = 60; // Header with autoplay toggle
				bottomPlayerHeight = 215;
				maxTracks = Math.min(8, Math.floor((height - bottomPlayerHeight - headerHeight ) / trackRowHeight));
			} else {
				// Small screens - calculate based on available space
				trackRowHeight = 58;
				headerHeight = 36; // Header with autoplay toggle
				bottomPlayerHeight = 160;
				maxTracks = Math.min(5, Math.floor((height - bottomPlayerHeight - headerHeight-40) / trackRowHeight));
			}

			// Ensure we show at least 2 tracks and no more than 5 on smaller screens
			maxTracks = Math.max(1, Math.min(maxTracks, 7));

			// Calculate the actual content height
			const calculatedHeight = maxTracks * trackRowHeight + headerHeight;

			// Add some padding and ensure minimum height
			const finalContentHeight = Math.max(calculatedHeight, 80); // Minimum 3 tracks worth
			const finalContainerHeight = finalContentHeight; // content + header + padding
			
			setContentHeight(`${finalContentHeight}px`);
			setContainerHeight(`${finalContainerHeight-headerHeight}px`);
		};

		calculateDimensions();
		window.addEventListener("resize", calculateDimensions);
		return () => window.removeEventListener("resize", calculateDimensions);
	}, []);

	useEffect(() => {
		// Handle fade-in and fade-out animations based on `isOpen` prop
		if (isOpen) {
			setShouldRender(true);
			setFadeClass("animate-bottom-to-top-opacity");
		} else {
			setFadeClass("duration-300 animate-top-to-bottom-opacity");
			const timeout = setTimeout(() => {
				setShouldRender(false); // Delay unmounting to allow animation to complete
			}, 300); // Match animation duration
			return () => clearTimeout(timeout); // Cleanup timeout
		}
	}, [isOpen]);

	const { playerQueue } = useSupremeEditsPlayer(); // Access playerQueue from context

	if (!shouldRender) return null; // Prevent rendering if `shouldRender` is false

	return (
		<aside
			className={`fixed left-0 bottom-[160px]  sm:bottom-[180px] md:bottom-[206px] lg:bottom-[113px] lg:h-[73vh] lg:max-h-[88vh] w-[380px] max-w-full z-[3999] text-white shadow-2xl rounded-l-lg ${fadeClass}`}
			style={{
				backdropFilter: "blur(10px)",
				left: 0,
				height: `calc(${contentHeight})`, // content height + header + padding
				maxHeight: "90vh",
			}}
		>
			{/* Background overlay */}
			<div className="z-50 absolute bg-[#0E0E11]/80 w-full h-full rounded-l-lg" />

			{/* Header section */}
			<div className="z-[60] relative flex items-center justify-between px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-5 overflow-y-auto">
				<h2 className="text-white text-xs sm:text-sm lg:text-base font-bold uppercase tracking-wider">
					Player Queue
				</h2>
				<div className="flex lg:hidden items-center gap-2 pl-2 sm:pl-4 lg:pl-6">
					<span className="text-xs sm:text-sm text-secondary">Autoplay</span>
					<label className="relative inline-flex items-center cursor-pointer w-9 h-5 sm:w-11 sm:h-6">
						<input
							type="checkbox"
							className="sr-only peer"
							checked={autoPlay}
							onChange={() => setAutoPlay((prev) => !prev)}
							aria-label="Toggle Autoplay"
						/>
						<div className="w-full h-full bg-[#1E1E1E] rounded-full peer-checked:bg-cyan-500 transition-colors" />
						<div className="absolute top-0.5 left-0.5 sm:top-1 sm:left-1 w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full transition-transform peer-checked:translate-x-[16px] sm:peer-checked:translate-x-[20px]" />
					</label>
				</div>

				<button
					onClick={onClose}
					className="text-gray-400 hover:text-white transition"
				>
					<IoMdClose className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
				</button>
			</div>

			{/* Content section */}
			<div
				className="z-[60] relative flex flex-1 items-start justify-center text-center px-3 sm:px-4 lg:px-6 pt-0 sm:pt-3 lg:pt-4 overflow-y-auto bg-[#0E0E11]/80"
				style={{
					height: containerHeight,
					maxHeight: contentHeight,
				}}
			>
				{playerQueue.length === 0 ? (
					// Display message when the queue is empty
					<div className="flex flex-row flex-wrap items-center justify-center gap-2 h-full w-full text-gray-500">
						<svg
							width="18"
							height="18"
							viewBox="0 0 19 17"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							className="w-4 h-4 sm:w-5 sm:h-5 lg:w-[18px] lg:h-[18px] opacity-30"
						>
							<path
								d="M0.894531 8.5H17.3945M0.894531 12.25H17.3945M0.894531 16H17.3945M2.76953 1H15.5195C16.0168 1 16.4937 1.19754 16.8454 1.54917C17.197 1.90081 17.3945 2.37772 17.3945 2.875C17.3945 3.37228 17.197 3.84919 16.8454 4.20083C16.4937 4.55246 16.0168 4.75 15.5195 4.75H2.76953C2.27225 4.75 1.79534 4.55246 1.44371 4.20083C1.09208 3.84919 0.894531 3.37228 0.894531 2.875C0.894531 2.37772 1.09208 1.90081 1.44371 1.54917C1.79534 1.19754 2.27225 1 2.76953 1Z"
								stroke="white"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<p className="text-sm sm:text-base text-[#565656] leading-none">
							Player queue is empty
						</p>
					</div>
				) : (
					// Render the list of tracks in the queue
					<div className="w-full h-full ">
						<ul className="flex flex-col items-start w-full">
							{playerQueue.map((track, i) => (
								<QueueTrackRow
									key={`${track.trackId}-${track.queueTrackId}`} // Use unique combination as key
									trackMetadata={track} // Pass track metadata
									remixMetadata={track.editInfos[0]} // Pass remix metadata if available
									playlistTracks={playerQueue} // Pass playlist tracks
									playIndex={i} // Pass index of the track
									thisPlaylistName="to-be-played"
								/>
							))}
						</ul>
					</div>
				)}
			</div>
		</aside>
	);
};

export default PlayerQueue;